import { z } from "zod";
import { automationIdSchema } from "../entities/automation.js";
import { automationArtifactIdSchema } from "../entities/automationArtifact.js";
import { automationRunIdSchema } from "../entities/automationRun.js";
import { playbookIdSchema } from "../entities/playbook.js";
import { signalIdSchema } from "../entities/signal.js";
import { teammateIdSchema } from "../entities/teammate.js";
import { gradingIdSchema } from "../entities/teammatePerformanceSummary.js";
import { topicIdSchema } from "../entities/topic.js";
const pageContextDescriptorSchema = z.discriminatedUnion("page", [
  z.object({
    page: z.literal("teammate"),
    playbookId: playbookIdSchema,
    teammateId: teammateIdSchema
  }),
  z.object({
    // The fallback for playbook-scoped pages with no more specific variant:
    // the dashboard, the team and group tabs, the month in review.
    page: z.literal("playbook"),
    playbookId: playbookIdSchema
  }),
  z.object({
    page: z.literal("customers")
  }),
  z.object({
    page: z.literal("playbookCalls"),
    playbookId: playbookIdSchema
  }),
  z.object({
    page: z.literal("playbookCriteria"),
    playbookId: playbookIdSchema
  }),
  z.object({
    page: z.literal("playbookCriterion"),
    playbookId: playbookIdSchema,
    /** Criterion ids are positions in a playbook's tree, not branded ids. */
    criterionId: z.string(),
    /** Set on the drill-downs that scope the criterion to one teammate. */
    teammateId: teammateIdSchema.optional()
  }),
  z.object({
    // The playbook's signals list, the section's landing page.
    page: z.literal("signals"),
    playbookId: playbookIdSchema
  }),
  z.object({
    // The signal's performance drill-down, not its editor: the user is reading
    // what the signal matched, so grounding it as configuration would push the
    // agent to propose edits to a signal they are only analyzing.
    page: z.literal("signalPerformance"),
    playbookId: playbookIdSchema,
    signalId: signalIdSchema
  }),
  z.object({
    page: z.literal("performanceReport"),
    playbookId: playbookIdSchema,
    /** A report template id (`template:performance`), not a row id. */
    reportId: z.string()
  }),
  z.object({
    // The grading slideover, open over whichever page the user reached it
    // from. Only the grading id: hydration reads the teammate and playbook off
    // the row, so the four route trees that mount the slideover all describe
    // it the same way.
    page: z.literal("customerInteraction"),
    gradingId: gradingIdSchema
  }),
  z.object({
    page: z.literal("playbookConfiguration"),
    /**
     * Absent while the user is creating a playbook that is not saved yet. The
     * editor serves `/settings/playbooks/new` through its `:playbookId` route,
     * so the literal `new` reaches the handle in place of an id.
     */
    playbookId: playbookIdSchema.optional()
  }),
  z.object({
    page: z.literal("teammateAgent"),
    /** Voice agents are teammates of type AGENT. */
    teammateAgentId: teammateIdSchema
  }),
  z.object({
    page: z.literal("signalConfiguration"),
    /** Absent while the user is creating a signal that is not saved yet. */
    signalId: signalIdSchema.optional()
  }),
  z.object({
    // The playbook's automations list, the section's landing page. The
    // editor and the run and report pages under it carry their own variants.
    page: z.literal("automations"),
    playbookId: playbookIdSchema
  }),
  z.object({
    page: z.literal("automationConfiguration"),
    /** The playbook the editor is nested under, which seeds a new automation. */
    playbookId: playbookIdSchema,
    /** Absent while the user is creating an automation that is not saved yet. */
    automationId: automationIdSchema.optional()
  }),
  z.object({
    // One execution of an automation. A standalone page beside the detail
    // tabs, not under them, which is why it carries its own variant rather
    // than an optional field on the configuration one.
    page: z.literal("automationRun"),
    automationId: automationIdSchema,
    runId: automationRunIdSchema
  }),
  z.object({
    // A saved report, reached from the automation's Results list or the
    // notification email's "View report" link.
    page: z.literal("automationArtifact"),
    automationId: automationIdSchema,
    artifactId: automationArtifactIdSchema
  }),
  z.object({
    // Named for the project, not the training: one configuration route serves
    // recruiting and training projects alike, and the descriptor is built from
    // the URL, which does not say which kind this is. Hydration resolves the
    // project and grounds each kind on its own terms.
    page: z.literal("projectConfiguration"),
    /** The project whose configuration tab is open — training or recruiting. */
    projectId: z.uuid()
  }),
  z.object({
    page: z.literal("knowledgeBaseDocument"),
    knowledgeBaseId: z.uuid(),
    /** The document open in the editor. Ids are not branded for attachments. */
    attachmentId: z.uuid()
  }),
  z.object({
    // A voice agent tool's details page, where its settings are edited.
    page: z.literal("toolConfiguration"),
    /** Tool ids are not branded. */
    toolId: z.uuid()
  }),
  z.object({
    page: z.literal("topics"),
    playbookId: playbookIdSchema,
    /** Set only when the user has drilled into one topic's page. */
    topicId: topicIdSchema.optional()
  })
]);
export {
  pageContextDescriptorSchema
};
//# sourceMappingURL=pageContext.js.map
